import {
  createUserWithEmailAndPassword,
  sendEmailVerification,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut as fbSignOut,
  GoogleAuthProvider,
} from "firebase/auth";
import type { User } from "firebase/auth";
import {
  collection,
  doc,
  getDoc,
  serverTimestamp,
  writeBatch,
} from "firebase/firestore";
import { auth, db } from "../firebase";

const googleProvider = new GoogleAuthProvider();

export interface SignUpArgs {
  email: string;
  password: string;
  yardName: string;
  displayName?: string;
}

export interface SignUpResult {
  userId: string;
  yardId: string;
  isNewUser: boolean;
}

export interface SignInArgs {
  email: string;
  password: string;
}

export interface GoogleAuthResult {
  userId: string;
  yardId: string | null;
  isNewUser: boolean;
}

interface CreateUserAndYardArgs {
  userId: string;
  email: string;
  displayName: string;
  yardName: string;
}

export async function signUpAndCreateYard({
  email,
  password,
  yardName,
  displayName,
}: SignUpArgs): Promise<SignUpResult> {
  const cred = await createUserWithEmailAndPassword(auth, email, password);
  const userId = cred.user.uid;
  const yardId = await createUserAndYard({
    userId,
    email,
    displayName: (displayName && displayName.trim()) || email.split("@")[0]!,
    yardName,
  });
  // Fire-and-forget — verification is non-blocking; the dashboard banner
  // exposes a manual resend.
  sendEmailVerification(cred.user).catch(() => undefined);
  return { userId, yardId, isNewUser: true };
}

export async function resendVerificationEmail(user: User): Promise<void> {
  if (user.emailVerified) return;
  await sendEmailVerification(user);
}

export async function signInWithEmail({ email, password }: SignInArgs): Promise<string> {
  const cred = await signInWithEmailAndPassword(auth, email, password);
  return cred.user.uid;
}

export async function signInOrSignUpWithGoogle({ yardName }: { yardName?: string } = {}): Promise<GoogleAuthResult> {
  const result = await signInWithPopup(auth, googleProvider);
  const user = result.user;
  const userSnap = await getDoc(doc(db, "users", user.uid));
  let yardId: string | null = null;
  if (!userSnap.exists()) {
    if (!yardName) {
      throw new Error("MISSING_YARD_NAME");
    }
    yardId = await createUserAndYard({
      userId: user.uid,
      email: user.email ?? "",
      displayName: user.displayName ?? (user.email ?? "user").split("@")[0]!,
      yardName,
    });
  }
  return { userId: user.uid, yardId, isNewUser: !userSnap.exists() };
}

export async function signOut(): Promise<void> {
  await fbSignOut(auth);
}

async function createUserAndYard({
  userId,
  email,
  displayName,
  yardName,
}: CreateUserAndYardArgs): Promise<string> {
  const yardRef = doc(collection(db, "yards"));
  const userRef = doc(db, "users", userId);

  const batch = writeBatch(db);
  batch.set(userRef, {
    email,
    displayName,
    createdAt: serverTimestamp(),
  });
  batch.set(yardRef, {
    name: yardName,
    ownerUserId: userId,
    members: [userId],
    createdBy: userId,
    createdAt: serverTimestamp(),
  });
  await batch.commit();
  return yardRef.id;
}
